import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  Button,
} from "@mui/material";
import { systemAPI, bookingAPI, inventoryAPI } from "../../utils/api";

// Helper to parse Firestore Timestamp or Date-like values into JS Date
const parseTimestamp = (raw) => {
  if (raw instanceof Date) return raw;
  if (raw?.toDate instanceof Function) return raw.toDate();
  if (raw?.seconds !== undefined) return new Date(raw.seconds * 1000);
  if (raw?._seconds !== undefined) return new Date(raw._seconds * 1000);
  if (typeof raw === "string" || typeof raw === "number") return new Date(raw);
  return new Date();
};

const SystemLogbook = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchLogs = async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch system, booking, and inventory usage logs
      const [systemRes, bookingRes, usageRes] = await Promise.all([
        systemAPI.getSystemLogs(params),
        bookingAPI.getLogs(params),
        inventoryAPI.getAllUsageLogs(params),
      ]);
      // Map system logs
      const systemLogs = (systemRes.data || []).map((log) => {
        // Parse system log timestamp robustly
        const t = log.timestamp;
        let ts = new Date();
        if (t instanceof Date) {
          ts = t;
        } else if (t?._seconds) {
          ts = new Date(t._seconds * 1000);
        } else if (t?.seconds) {
          ts = new Date(t.seconds * 1000);
        } else if (typeof t === "string") {
          ts = new Date(t);
        } else if (t?.toDate instanceof Function) {
          ts = t.toDate();
        } else if (log.createdAt) {
          const c = log.createdAt;
          if (c instanceof Date) {
            ts = c;
          } else if (c?._seconds) {
            ts = new Date(c._seconds * 1000);
          } else if (c?.seconds) {
            ts = new Date(c.seconds * 1000);
          } else if (c?.toDate instanceof Function) {
            ts = c.toDate();
          }
        }
        return {
          id: log.id,
          timestamp: ts,
          performedBy: log.performedBy,
          type: log.type,
          message: log.message,
        };
      });
      // Map booking logs
      const bookingLogs = (bookingRes.data || []).map((log) => {
        // Parse booking log timestamp robustly
        const t = log.timestamp || log.createdAt;
        let ts = new Date();
        if (t instanceof Date) {
          ts = t;
        } else if (t?._seconds) {
          ts = new Date(t._seconds * 1000);
        } else if (t?.seconds) {
          ts = new Date(t.seconds * 1000);
        } else if (typeof t === "string") {
          ts = new Date(t);
        } else if (t?.toDate instanceof Function) {
          ts = t.toDate();
        }
        // Determine performedBy
        let performedBy = "";
        if (typeof log.userId === "object") {
          const userObj = log.userId;
          performedBy =
            userObj.name && userObj.name !== "User"
              ? userObj.name
              : userObj.email || userObj.id;
        } else {
          performedBy = log.userId;
        }
        // Get resource name
        const resourceName = String(
          typeof log.resourceId === "object"
            ? log.resourceId.name || log.resourceId.id
            : log.resourceId
        );
        return {
          id: log.id,
          timestamp: ts,
          performedBy,
          type: `booking-${log.activityType}`,
          message: `${log.activityType} booking for resource ${resourceName}`,
        };
      });
      // Map usage logs
      const usageLogs = (usageRes.data?.logs || usageRes.data || []).map(
        (log) => {
          // Determine timestamp: prefer log.timestamp, then createdAt, handling both _seconds and seconds fields
          let ts = new Date();
          const t = log.timestamp;
          if (t instanceof Date) {
            ts = t;
          } else if (t?._seconds) {
            ts = new Date(t._seconds * 1000);
          } else if (t?.seconds) {
            ts = new Date(t.seconds * 1000);
          } else if (t?.toDate instanceof Function) {
            ts = t.toDate();
          } else {
            const c = log.createdAt;
            if (c instanceof Date) {
              ts = c;
            } else if (c?._seconds) {
              ts = new Date(c._seconds * 1000);
            } else if (c?.seconds) {
              ts = new Date(c.seconds * 1000);
            } else if (c?.toDate instanceof Function) {
              ts = c.toDate();
            }
          }
          // Determine performedBy
          let performedBy = "";
          if (typeof log.userId === "object") {
            const userObj = log.userId;
            if (userObj.name && userObj.name !== "User") {
              performedBy = userObj.name;
            } else if (userObj.email) {
              performedBy = userObj.email;
            } else if (userObj.id) {
              performedBy = userObj.id;
            }
          } else {
            performedBy = log.userId;
          }
          // Determine item name and unit
          const itemName =
            typeof log.inventoryId === "object"
              ? log.inventoryId.name || log.inventoryId.id
              : log.inventoryId;
          // Use the requested unit from log or fallback to item record unit
          const itemUnit =
            log.requestUnit ||
            (typeof log.inventoryId === "object" ? log.inventoryId.unit : "") ||
            "";
          return {
            id: log.id,
            timestamp: ts,
            performedBy,
            type: log.action || log.type,
            message:
              log.message ||
              `${log.action === "subtract" ? "Subtracted" : "Added"} ${
                log.quantity || ""
              }${itemUnit ? " " + itemUnit : ""} of item ${itemName}`,
          };
        }
      );
      // Combine and sort
      const combined = [...systemLogs, ...bookingLogs, ...usageLogs].sort(
        (a, b) => b.timestamp - a.timestamp
      );
      setLogs(combined);
    } catch (err) {
      console.error("[SystemLogbook] Error fetching logs:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    if (filter) {
      setFilteredLogs(
        logs.filter((log) =>
          log.message.toLowerCase().includes(filter.toLowerCase())
        )
      );
    } else {
      setFilteredLogs(logs);
    }
  }, [filter, logs]);

  const handleFilter = () => {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    fetchLogs(params);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        System Logbook
      </Typography>

      {/* Filter Controls */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={handleFilter}>
          Apply Filters
        </Button>
      </Box>

      <TextField
        label="Filter by message"
        variant="outlined"
        size="small"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        sx={{ mb: 2, width: 300 }}
      />

      <Button
        variant="contained"
        onClick={() => setFilter("")}
        sx={{ mb: 2, ml: 1 }}
      >
        Clear Filter
      </Button>

      {loading && <CircularProgress />}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date & Time</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id || log._id}>
                  <TableCell>
                    {parseTimestamp(log.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell>{log.performedBy || log.userId}</TableCell>
                  <TableCell>{log.type}</TableCell>
                  <TableCell>{log.message}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default SystemLogbook;
